import { Connection, Edge } from 'reactflow';

/**
 * Função auxiliar para extrair tipo do nó
 */
export const getNodeType = (nodeId: string): string => {
  if (nodeId.startsWith('splitter-')) return 'splitter';
  if (nodeId.startsWith('cabo-')) return 'cabo';
  if (nodeId.startsWith('dio-')) return 'dio';
  if (nodeId.startsWith('olt-')) return 'olt';
  if (nodeId.startsWith('input-')) return 'input';
  if (nodeId.startsWith('output-')) return 'output';
  return 'unknown';
};

/**
 * Função auxiliar para verificar se é um nó de cabo (inclui caboOut)
 */
export const isCaboNode = (nodeId: string): boolean => {
  return nodeId.startsWith('cabo-');
};

/**
 * Função para validar conexões baseada nas regras de negócio:
 * 
 * PERMITIDO:
 * ✅ Saída de splitter → saída de cabo (bidirecionais)
 * ✅ Saída de cabo → saída de splitter (bidirecionais)
 * ✅ Entrada de qualquer nó → saída de qualquer nó (normal)
 * ✅ Conexões internas em cabos (FO com FO do mesmo cabo)
 * ✅ Qualquer conexão que não seja explicitamente bloqueada
 * 
 * BLOQUEADO:
 * ❌ Saída de splitter → saída de splitter
 * ❌ Auto-conexão no mesmo handle
 * ❌ Mais de uma conexão no mesmo handle (cada entrada/saída de cabo ou splitter aceita só 1 conexão)
 */

/** Verifica se algum edge já usa o par (nodeId, handleId) como source ou como target */
function isHandleUsedInEdges(
  nodeId: string,
  handleId: string | null | undefined,
  edges: Edge[],
  asSource: boolean
): boolean {
  const handle = handleId ?? undefined;
  return edges.some((e) => {
    if (asSource) return e.source === nodeId && (e.sourceHandle ?? undefined) === handle;
    return e.target === nodeId && (e.targetHandle ?? undefined) === handle;
  });
}

export const validateConnection = (connection: Connection, existingEdges?: Edge[]): boolean => {
  // Validações básicas obrigatórias
  if (!connection.source || !connection.target) {
    return false;
  }

  // Não permitir auto-conexão no mesmo handle
  if (connection.source === connection.target &&
    connection.sourceHandle === connection.targetHandle) {
    return false;
  }

  // Cada handle aceita apenas 1 conexão: origem e destino não podem estar já em uso
  if (existingEdges && existingEdges.length > 0) {
    if (isHandleUsedInEdges(connection.source, connection.sourceHandle, existingEdges, true)) {
      return false; // Saída/origem já conectada
    }
    if (isHandleUsedInEdges(connection.target, connection.targetHandle, existingEdges, false)) {
      return false; // Entrada/destino já conectada
    }
  }

  // Extrair tipos dos nós
  const sourceType = getNodeType(connection.source);
  const targetType = getNodeType(connection.target);

  // Verificar se são handles de saída
  const isSourceOutput = Boolean(connection.sourceHandle?.includes('output') || connection.sourceHandle?.includes('fo'));
  const isTargetOutput = Boolean(connection.targetHandle?.includes('output') || connection.targetHandle?.includes('fo'));

  // PERMITIR conexões internas em cabos (mesmo nó, handles diferentes) — desde que os handles não estejam em uso
  if (connection.source === connection.target && isCaboNode(connection.source)) {
    if (connection.sourceHandle !== connection.targetHandle) {
      return true;
    }
  }

  // ÚNICA REGRA DE BLOQUEIO: Auto-conexão no mesmo nó e mesma porta
  if (connection.source === connection.target && connection.sourceHandle === connection.targetHandle) {
    return false;
  }

  return true;
};

/**
 * Função para determinar se uma conexão deve usar o edge bidirecionais
 */
export const shouldUseBidirectionalEdge = (connection: Connection): boolean => {
  if (!connection.source || !connection.target) return false;

  const sourceType = getNodeType(connection.source);
  const targetType = getNodeType(connection.target);

  const isSourceOutput = Boolean(connection.sourceHandle?.includes('output') || connection.sourceHandle?.includes('fo'));
  const isTargetOutput = Boolean(connection.targetHandle?.includes('output') || connection.targetHandle?.includes('fo'));

  // Conexões internas em cabos (sempre bidirecionais)
  if (connection.source === connection.target && isCaboNode(connection.source)) {
    return true;
  }

  // Usar edge bidirecionais apenas para conexões saída → saída entre splitter e cabo
  const sourceCabo = isCaboNode(connection.source);
  const targetCabo = isCaboNode(connection.target);
  const sourceDio = connection.source.startsWith('dio-');
  const targetDio = connection.target.startsWith('dio-');

  return (sourceType === 'splitter' && targetCabo && isSourceOutput && isTargetOutput) ||
    (sourceCabo && targetType === 'splitter' && isSourceOutput && isTargetOutput) ||
    (sourceDio && targetCabo && isSourceOutput && isTargetOutput) ||
    (sourceCabo && targetDio && isSourceOutput && isTargetOutput) ||
    (sourceType === 'splitter' && targetDio && isSourceOutput && isTargetOutput) ||
    (sourceDio && targetType === 'splitter' && isSourceOutput && isTargetOutput);
}; 